import { useCallback, useEffect, useState, type JSX } from 'react';
import type { ProjectSummary } from '../../../persist/api';
import { Dialog } from '../../components/Dialog';
import { useActions, useRuntime, useStudio } from '../../context';
import { formatDateTime } from '../../format';
import { safely } from '../../safe';

/** Loads the saved collections and reloads whenever the open collection changes or is saved. */
export function useCollections(): { list: ProjectSummary[]; loading: boolean; refresh(): void } {
  const studio = useStudio();
  const currentId = useRuntime((r) => r.collection.id);
  const savedAt = useRuntime((r) => r.collection.lastSavedAt);
  const [list, setList] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void safely(studio, () => studio.listCollections(), 'Could not list scene collections').then((result) => {
      if (cancelled) return;
      if (result) setList([...result].sort((a, b) => b.updatedAt - a.updatedAt));
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [studio, currentId, savedAt, nonce]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);
  return { list, loading, refresh };
}

export function CollectionsDialog({ onClose }: { onClose(): void }): JSX.Element {
  const actions = useActions();
  const currentId = useRuntime((r) => r.collection.id);
  const { list, loading } = useCollections();
  return (
    <Dialog
      title="Scene collections"
      onClose={onClose}
      width={620}
      height={460}
      footer={
        <>
          <button type="button" className="ui-btn" onClick={() => actions.newCollection()}>
            New…
          </button>
          <button type="button" className="ui-btn" onClick={() => actions.importCollection()}>
            Import…
          </button>
          <span style={{ flex: 1 }} />
          <button type="button" className="ui-btn primary" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      {loading ? <div className="ui-empty">Loading…</div> : null}
      {!loading && list.length === 0 ? <div className="ui-empty">No saved collections.</div> : null}
      {list.length > 0 ? (
        <table className="ui-table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Scenes</th>
              <th scope="col">Last changed</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {list.map((entry) => (
              <tr key={entry.id}>
                <th scope="row">
                  {entry.name} {entry.id === currentId ? <span className="ui-badge preview">OPEN</span> : null}
                </th>
                <td>{entry.sceneCount}</td>
                <td>{formatDateTime(entry.updatedAt)}</td>
                <td>
                  <div className="ui-row" style={{ justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className="ui-btn"
                      disabled={entry.id === currentId}
                      onClick={() => {
                        onClose();
                        actions.openCollection(entry.id);
                      }}
                    >
                      Open
                    </button>
                    <button
                      type="button"
                      className="ui-btn"
                      disabled={entry.id === currentId}
                      title={entry.id === currentId ? 'Open another collection before deleting this one' : undefined}
                      onClick={() => actions.deleteCollection(entry.id, entry.name)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </Dialog>
  );
}
